import type { SxProps, Theme } from "@mui/material";
import SelectFormComponent from "components/shared/FormComponent/SelectFormComponent";
import { isBookPublisher } from "helpers/publisher";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect } from "react";
import type { Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { fetchAdvanceCustomStatuses } from "store/advanceCustomStatusStore";
import { useAppDispatch, useAppSelector } from "store/store";

import type { TAdvancePayment } from "../NewAdvanceModal";

export const formatAdvanceCustomStatusValue = (value: number | string | null | undefined): string => {
    if (value == null || value === "") return "";
    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? String(id) : "";
};

export const toAdvanceCustomStatusId = (value: string | number | null | undefined): number | null => {
    const formatted = formatAdvanceCustomStatusValue(value);
    return formatted === "" ? null : Number(formatted);
};

const AdvanceCustomStatusField = ({
    control,
    controlName,
    disabled = false,
    sx,
}: {
    control: Control<TAdvancePayment>;
    controlName: string;
    disabled?: boolean;
    sx?: SxProps<Theme>;
}) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { companyInfos } = useCompanySettings();
    const bookPublisher = isBookPublisher(companyInfos?.publisherType);
    const statuses = useAppSelector((state) => state.advanceCustomStatuses.items);
    const fetchStatus = useAppSelector((state) => state.advanceCustomStatuses.status);

    useEffect(() => {
        if (!bookPublisher || fetchStatus !== "idle") return;
        void dispatch(fetchAdvanceCustomStatuses());
    }, [dispatch, fetchStatus, bookPublisher]);

    if (!bookPublisher) return null;

    return (
        <SelectFormComponent
            placeholder={t("AdvancePayment.CustomStatus")}
            control={control}
            controlName={controlName}
            items={[
                { value: "", label: t("AdvancePayment.NoCustomStatus") },
                ...statuses.map((status) => ({ value: String(status.id), label: status.name })),
            ]}
            disabled={disabled}
            sx={sx}
        />
    );
};

export default AdvanceCustomStatusField;
